// Ghostwriter Domain Layout - Navigation wrapper for aighostwriterpro.com pages
import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { BookOpen, Menu, X } from 'lucide-react';
import { Button } from './ui/button';

const NAV_LINKS = [
  { href: '/', label: 'Home' },
  { href: '/how-it-works', label: 'How It Works' },
  { href: '/pricing', label: 'Pricing' },
  { href: '/faq', label: 'FAQ' },
  { href: '/about', label: 'About' },
  { href: '/contact', label: 'Contact' },
];

export function GhostwriterLayout({ children }) {
  const { user } = useAuth();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);

  return (
    <div className="min-h-screen flex flex-col">
      {/* Header */}
      <header className="bg-slate-900/95 backdrop-blur-sm border-b border-slate-800 sticky top-0 z-50">
        <div className="container mx-auto px-4 flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-2" data-testid="gw-logo">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-r from-purple-600 to-pink-600 flex items-center justify-center">
              <BookOpen className="w-4 h-4 text-white" />
            </div>
            <span className="text-white font-bold text-lg">AI Ghostwriter Pro</span>
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-1" data-testid="gw-nav">
            {NAV_LINKS.map(link => (
              <Link
                key={link.href}
                to={link.href}
                className={`px-3 py-2 rounded-lg text-sm transition-colors ${
                  location.pathname === link.href
                    ? 'text-white bg-slate-800'
                    : 'text-gray-400 hover:text-white hover:bg-slate-800/50'
                }`}
              >
                {link.label}
              </Link>
            ))}
            {user ? (
              <Link to="/ghostwriter/dashboard">
                <Button size="sm" className="ml-2 bg-gradient-to-r from-purple-600 to-pink-600">Dashboard</Button>
              </Link>
            ) : (
              <a href="https://upost.com/login?redirect=/ghostwriter/dashboard">
                <Button size="sm" className="ml-2 bg-gradient-to-r from-purple-600 to-pink-600">Get Started</Button>
              </a>
            )}
          </nav>

          {/* Mobile Menu Button */}
          <button className="md:hidden text-white p-2" onClick={() => setMenuOpen(!menuOpen)} aria-label="Toggle menu" data-testid="gw-menu-toggle">
            {menuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Nav */}
        {menuOpen && (
          <nav className="md:hidden bg-slate-900 border-t border-slate-800 py-4 px-4 space-y-1" data-testid="gw-mobile-nav">
            {NAV_LINKS.map(link => (
              <Link
                key={link.href}
                to={link.href}
                onClick={() => setMenuOpen(false)}
                className={`block px-4 py-3 rounded-lg text-sm ${
                  location.pathname === link.href ? 'text-white bg-slate-800' : 'text-gray-400 hover:text-white'
                }`}
              >
                {link.label}
              </Link>
            ))}
            {user ? (
              <Link to="/ghostwriter/dashboard" onClick={() => setMenuOpen(false)} className="block px-4 py-3 text-purple-400 font-medium">Dashboard</Link>
            ) : (
              <a href="https://upost.com/login?redirect=/ghostwriter/dashboard" className="block px-4 py-3 text-purple-400 font-medium">Get Started</a>
            )}
          </nav>
        )}
      </header>

      {/* Page Content */}
      <main className="flex-1">{children}</main>

      {/* Footer */}
      <footer className="bg-slate-900 border-t border-slate-800 py-12">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <div className="w-8 h-8 rounded-lg bg-gradient-to-r from-purple-600 to-pink-600 flex items-center justify-center">
                  <BookOpen className="w-4 h-4 text-white" />
                </div>
                <span className="text-white font-bold">AI Ghostwriter Pro</span>
              </div>
              <p className="text-gray-500 text-sm">Professional AI book writing service. From idea to published manuscript in days.</p>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-3 text-sm">Service</h4>
              <div className="space-y-2">
                <Link to="/how-it-works" className="block text-gray-500 text-sm hover:text-gray-300">How It Works</Link>
                <Link to="/pricing" className="block text-gray-500 text-sm hover:text-gray-300">Pricing</Link>
                <Link to="/faq" className="block text-gray-500 text-sm hover:text-gray-300">FAQ</Link>
              </div>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-3 text-sm">Company</h4>
              <div className="space-y-2">
                <Link to="/about" className="block text-gray-500 text-sm hover:text-gray-300">About</Link>
                <Link to="/contact" className="block text-gray-500 text-sm hover:text-gray-300">Contact</Link>
                <a href="https://upost.com" className="block text-gray-500 text-sm hover:text-gray-300">UPost Platform</a>
              </div>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-3 text-sm">Legal</h4>
              <div className="space-y-2">
                <a href="https://upost.com/terms" className="block text-gray-500 text-sm hover:text-gray-300">Terms of Service</a>
                <a href="https://upost.com/privacy" className="block text-gray-500 text-sm hover:text-gray-300">Privacy Policy</a>
              </div>
            </div>
          </div>
          <div className="border-t border-slate-800 pt-6 flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-gray-600 text-xs">&copy; 2026 Web-Tech Services. All rights reserved.</p>
            <p className="text-gray-600 text-xs">Arlington, Texas | <a href="https://upost.com" className="hover:text-gray-400">Powered by UPost</a></p>
          </div>
        </div>
      </footer>
    </div>
  );
}
